import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
} from '@mui/material';

interface UseCasesModalProps {
  open: boolean;
  onClose: () => void;
}

const cases: Array<{ title: string; input: string; description: string }> = [
  { title: 'Workout Form Coach', input: 'video', description: 'Upload a workout video (e.g., push‑ups). Get notes on form, strengths, improvements, and filming angle suggestions.' },
  { title: 'Homework Checker', input: 'pdf/file', description: 'Upload a homework PDF and request checks for logic, spelling, math steps, with a corrected version.' },
  { title: 'Receipt → Expense JSON', input: 'image', description: 'Photo a receipt; extract merchant, date, line items, taxes, totals into normalized expense JSON.' },
  { title: 'Meeting Minutes Summarizer', input: 'audio', description: 'Upload meeting audio; get a structured summary with decisions, action items, owners, and due dates.' },
  { title: 'Document Summarizer', input: 'pdf', description: 'Summarize long PDFs (reports, papers) into key points, sentiment, glossary, and follow‑up questions.' },
  { title: 'Resume → Candidate Profile', input: 'pdf', description: 'Parse resumes into a unified candidate profile with skills, experience, education, and ATS keywords.' },
  { title: 'Product Review Insights', input: 'json/text', description: 'Analyze a list of product reviews to extract common themes, pros/cons, sentiment, and feature requests.' },
  { title: 'Visual Defect Detection', input: 'image', description: 'Upload manufacturing photos; detect defects, severity, and remediation guidance.' },
  { title: 'Menu → Nutrition Extractor', input: 'image', description: 'Upload a menu snapshot; extract items with calories/macros if present; flag items needing lookup.' },
  { title: 'Invoice → Accounting Entries', input: 'pdf', description: 'Convert invoices into accounting entries with vendor, GL codes, amounts, and payment terms.' },
  { title: 'Code Snippet Reviewer', input: 'text/file', description: 'Paste or upload code; get review notes, complexity highlights, tests to add, and refactor suggestions.' },
  { title: 'Interview Analyzer', input: 'audio/video', description: 'Analyze recorded interviews; extract topics, candidate signals, and recommended follow‑ups.' },
  { title: 'Legal Clause Extractor', input: 'pdf', description: 'Extract specific clauses (termination, liability, IP) from contracts into normalized fields.' },
  { title: 'RFP Compliance Checker', input: 'pdf', description: 'Check RFP responses for compliance; produce checklist, gaps, and remediation draft text.' },
  { title: 'Social Media Content Planner', input: 'text', description: 'Provide a campaign brief; get a calendar with post ideas, captions, and asset prompts.' },
  { title: 'Support Ticket Router', input: 'json/text', description: 'Normalize tickets and auto‑route by product, priority, and sentiment; propose responses.' },
  { title: 'Medical Note Structurer', input: 'audio/text', description: 'Transcribe/ingest notes to extract HPI, meds, vitals, assessments, and ICD‑10 suggestions.' },
  { title: 'Research Paper Comparator', input: 'pdf', description: 'Upload multiple PDFs; extract methods, datasets, metrics, and produce a comparison table.' },
  { title: 'Food Diary Analyzer', input: 'image/text', description: 'Upload meal photos or notes; identify foods, estimate macros, and suggest improvements.' },
  { title: 'Safety Incident Report', input: 'image/video/text', description: 'Ingest photos/videos/notes; extract incident details, severity, root cause hypotheses, and actions.' },
];

export const UseCasesModal: React.FC<UseCasesModalProps> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>What can I build? Real‑world examples</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {cases.map((c) => (
            <Grid item xs={12} md={6} key={c.title}>
              <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{c.title}</Typography>
                  <Chip label={`Input: ${c.input}`} size="small" color="primary" variant="outlined" />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {c.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};
